import { FileData, LanguageProficiencyMetaData } from '@applyboard/school-applications-types-lib'
import { DocumentTags } from '../../../utils/enums'
import { additionalDocumentTypeOptions } from '../../../utils'
import { SetAllFieldsOptional } from '@applyboard/school-backend-lib'
import { StudentApplication } from '../types'

type FilesOfTypeOptions = {
  activeFiles?: boolean
}

export function getFilesOfType(
  fileTypes: DocumentTags[],
  fileData?: FileData,
  options?: FilesOfTypeOptions,
): FileData {
  return Object.entries(fileData || {}).reduce((acc, [id, fileData]) => {
    if (!fileData) return acc
    if (fileTypes.includes(fileData.type as unknown as DocumentTags)) {
      if (options?.activeFiles && !fileData.activeRecord) {
        return acc
      }
      acc[id] = fileData
    }
    return acc
  }, {} as FileData)
}

export function getFilesOfTypeLanguageProficiency(files?: FileData) {
  return getFilesOfType(
    [
      DocumentTags.LANGUAGE_TEST_IELTS,
      DocumentTags.LANGUAGE_TEST_TOEFL,
      DocumentTags.LANGUAGE_TEST_PTE,
      DocumentTags.LANGUAGE_TEST_CAEL,
      DocumentTags.LANGUAGE_TEST_MET,
      DocumentTags.LANGUAGE_TEST_CAMBRIDGE,
      DocumentTags.LANGUAGE_TEST_EPP,
      DocumentTags.LANGUAGE_TEST_OTHER,
      DocumentTags.LANGUAGE_TEST_DUOLINGO,
      DocumentTags.LANGUAGE_TEST_CANTEST,
      DocumentTags.LANGUAGE_TEST_LANGUAGECERT_ESOL,
      DocumentTags.LANGUAGE_TEST_LEAP_4_ADVANCED,
    ],
    files as FileData,
  )
}

export function getFilesOfTypeAdditionalDocuments(files?: FileData) {
  return getFilesOfType(
    Object.keys(additionalDocumentTypeOptions) as DocumentTags[],
    files as FileData,
  )
}

export function getFilesOfTypeDecision(files?: FileData) {
  return getFilesOfType(
    [
      DocumentTags.PRE_OFFER_LETTER,
      DocumentTags.FINAL_OFFER_LETTER,
      DocumentTags.WAITLIST_LETTER,
      DocumentTags.DECLINED_LETTER,
    ],
    files as FileData,
    // No activeFiles option - shows all files regardless of active status
  )
}

/**
 * Checks if the application uses the new language proficiency schema structure
 *
 * @param application - The student application object to check
 * @returns { languageProficiencyRecordIdFromApplication: string | undefined; hasNewLanguageProficiencySchema: boolean }
 * object with the language proficiency record id from the application and a boolean indicating if the application uses the new language proficiency schema
 */
export function hasNewLanguageProficiencySchema(application?: StudentApplication): {
  languageProficiencyRecordIdFromApplication: string | undefined
  hasNewLanguageProficiencyRecord: boolean
} {
  const languageProficiencyRecordIdFromApplication =
    application?.attributes?.languageProficiency?.languageProficiencyData?.[
      Object.keys(application?.attributes?.languageProficiency?.languageProficiencyData)[0]
    ]?.languageProficiencyId
  if (!languageProficiencyRecordIdFromApplication) {
    return {
      languageProficiencyRecordIdFromApplication: undefined,
      hasNewLanguageProficiencyRecord: false,
    }
  }
  return { languageProficiencyRecordIdFromApplication, hasNewLanguageProficiencyRecord: true }
}

/**
 * Checks if the application uses old language proficiency schema structure
 *
 * @param application - The student application object to check
 * @returns { languageProficiencyRecordIdFromApplication: string | undefined; applicationHasLegacyLanguageProficiency: boolean }
 * object with the language proficiency data from the application and a boolean indicating if the application uses the old language proficiency schema
 */
export function hasLegacyLanguageProficiencySchema(application?: StudentApplication): {
  legacyLanguageProficiencyData:
    | SetAllFieldsOptional<LanguageProficiencyMetaData | null>
    | undefined
  applicationHasLegacyLanguageProficiency: boolean
} {
  const legacyLanguageProficiencyData =
    application?.attributes?.languageProficiency?.languageProficiencyData?.[
      Object.keys(application?.attributes?.languageProficiency?.languageProficiencyData)[0]
    ]
  if (!legacyLanguageProficiencyData || legacyLanguageProficiencyData?.languageProficiencyId) {
    return {
      legacyLanguageProficiencyData: undefined,
      applicationHasLegacyLanguageProficiency: false,
    }
  }
  return { legacyLanguageProficiencyData, applicationHasLegacyLanguageProficiency: true }
}
